import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowPathIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { Liquid } from "liquidjs";
import {
  injectMockProjectDetails,
  mockProject,
  preparePreviewHtml,
  replaceLiquidVariables,
} from "../lib/preview-mock";

type DeviceMode = "desktop" | "tablet" | "mobile";

interface DevicePreset {
  id: DeviceMode;
  label: string;
  width: string;
  pxLabel: string;
  Icon: typeof ComputerDesktopIcon;
}

const DEVICES: DevicePreset[] = [
  {
    id: "desktop",
    label: "سطح المكتب",
    width: "100%",
    pxLabel: "Full",
    Icon: ComputerDesktopIcon,
  },
  {
    id: "tablet",
    label: "تابلت",
    width: "768px",
    pxLabel: "768",
    Icon: DeviceTabletIcon,
  },
  {
    id: "mobile",
    label: "جوال",
    width: "375px",
    pxLabel: "375",
    Icon: DevicePhoneMobileIcon,
  },
];

const DEBOUNCE_MS = 400;

interface Props {
  rawCode: string;
  isPublished?: boolean;
}

const engine = new Liquid({ cache: false });

export const ThemeVisualPreview = ({ rawCode, isPublished }: Props) => {
  const [device, setDevice] = useState<DeviceMode>("desktop");
  const [srcDoc, setSrcDoc] = useState<string>("");
  const [renderError, setRenderError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const requestIdRef = useRef(0);

  const activeDevice = useMemo(
    () => DEVICES.find((d) => d.id === device) ?? DEVICES[0],
    [device],
  );

  // Debounced render. Each effect cycle bumps a request id so an in-flight
  // render that finishes after a newer keystroke is dropped.
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsRendering(true);

    const timer = window.setTimeout(async () => {
      try {
        // Normalize Shopify-style inline comments (`{% # … %}`) to the
        // no-space form liquidjs reliably parses, then strip them outright as
        // a safety net so unsupported variants can't break rendering.
        const normalized = (rawCode || "")
          .replace(/\{%\s+#/g, "{%#")
          .replace(/\{%#[\s\S]*?%\}/g, "");

        let body: string;
        try {
          // Spread mockProject so bare placeholders like `{{ title }}` resolve,
          // AND keep `project` so prefixed `{{ project.title }}` also works.
          body = await engine.parseAndRender(normalized, {
            ...mockProject,
            project: mockProject,
          });
        } catch {
          // Liquid parse error — fall back to a simple regex replacer so the
          // admin still sees something readable while they fix the syntax.
          body = replaceLiquidVariables(normalized);
        }
        if (requestId !== requestIdRef.current) return;
        body = injectMockProjectDetails(body, {
          projectDetails: mockProject.projectDetails,
          imageUrl: mockProject.projectDescriptionImagesUrls[0] ?? null,
        });
        setSrcDoc(preparePreviewHtml(body));
        setRenderError(null);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setRenderError(
          err instanceof Error ? err.message : "تعذّر عرض المعاينة",
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setIsRendering(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [rawCode]);

  return (
    <div className="flex bg-white h-full max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-gray-200 shadow-sm dark:border-gray-700 dark:bg-gray-800/60">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/60 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
        <div className="flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-lg bg-purple-900 text-white dark:bg-purple-500">
            <EyeIcon className="size-4" />
          </span>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
              معاينة مباشرة
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              {activeDevice.label} · {activeDevice.pxLabel}
              {activeDevice.pxLabel !== "Full" && "px"}
              {isRendering && (
                <span className="ms-2 inline-flex items-center gap-1 text-purple-700 dark:text-purple-300">
                  <ArrowPathIcon className="size-3 animate-spin" />
                  تحديث...
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isPublished !== undefined && (
            <span
              className={`hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 sm:inline-flex ${
                isPublished
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800"
                  : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800"
              }`}
            >
              <span
                className={`size-1.5 rounded-full ${
                  isPublished ? "bg-emerald-500" : "bg-amber-500"
                }`}
              />
              {isPublished ? "منشور" : "مسودة"}
            </span>
          )}

          <div
            role="tablist"
            aria-label="أبعاد المعاينة"
            className="inline-flex items-center gap-1 rounded-2xl bg-gray-100 p-1 dark:bg-gray-900"
          >
            {DEVICES.map((d) => {
              const Icon = d.Icon;
              const isActive = d.id === device;
              return (
                <button
                  key={d.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  title={`${d.label} (${d.pxLabel}${d.pxLabel !== "Full" ? "px" : ""})`}
                  onClick={() => setDevice(d.id)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                    isActive
                      ? "bg-white text-purple-900 shadow-sm dark:bg-gray-700 dark:text-purple-300"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  <Icon className="size-4" />
                  <span className="hidden sm:inline">{d.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stage */}
      <div className="relative flex flex-1 items-stretch justify-center overflow-auto bg-[radial-gradient(circle_at_1px_1px,_rgba(0,0,0,0.06)_1px,_transparent_0)] bg-[length:18px_18px] p-4 dark:bg-gray-900 dark:bg-[radial-gradient(circle_at_1px_1px,_rgba(255,255,255,0.05)_1px,_transparent_0)]">
        {renderError ? (
          <div className="m-auto max-w-md rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
            <p className="mb-1 flex items-center gap-2 font-semibold">
              <ExclamationTriangleIcon className="size-4" />
              تعذّر عرض المعاينة
            </p>
            <pre
              dir="ltr"
              className="overflow-auto whitespace-pre-wrap text-xs"
            >
              {renderError}
            </pre>
          </div>
        ) : (
          <div
            className="min-h-50 mx-auto h-full overflow-hidden rounded-lg transition-[width] duration-300 ease-out"
            style={{
              width: activeDevice.width,
              maxWidth: "100%",
            }}
          >
            <iframe
              key={device}
              title="Theme preview"
              srcDoc={srcDoc}
              allow-scripts={true}
              sandbox="allow-same-origin"
              className="border-0"
              style={{
                width: "100%",
                height: "100%",
                minHeight: "50vh",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
